import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { useArcheSettings } from '@/lib/hooks/useArcheSettings';
import { HolderSwitcher, useHolderSwitcher } from '@/components/HolderSwitcher';
import { useSelectedHolder } from '../hooks/useSelectedHolder';
import { ArcheHeader } from '../../../components/ArcheHeader';
import { useDocumentFiles, useEnvelope, useUpdateEnvelope, useDeleteEnvelope } from '../hooks';
import {
  DocumentGrid,
  DocumentUploader,
  FieldsEditor,
  RecipientsTable,
  type FieldsEditorHandle,
  type RecipientsTableHandle,
} from '../components';
import {
  DEFAULT_DOCUMENT_SIGN_SETTINGS,
  DOCUMENT_SIGN_ARCHE_ID,
  PLATFORM_DEFAULT_ENVELOPE_TITLE,
} from '../settings';
import type { DocumentSignSigningOrder } from '@/lib/api';

export function EnvelopeBuilderPage() {
  const { envelopeId } = useParams<{ envelopeId: string }>();
  const navigate = useNavigate();
  const { envelope, loading, error } = useEnvelope(envelopeId);
  const updateEnvelope = useUpdateEnvelope(envelopeId ?? '');
  const deleteEnvelope = useDeleteEnvelope();
  const { files, loading: filesLoading } = useDocumentFiles(envelopeId);

  // The envelope's holder is fixed at creation — surface it read-only. Resolve
  // a friendly display name from the workspace list when it's one the user can
  // still see; otherwise fall back to the holder id.
  const holderSwitcher = useHolderSwitcher();
  const holderName =
    holderSwitcher.options.find((o) => o.holderId === envelope?.holderId)?.displayName ?? null;

  // The global workspace chip shows the user's active workspace preference,
  // not the envelope's fixed holder. Switching the chip on this page updates
  // the platform-wide preference (which Fias 360 / Leads / the envelopes list
  // will respect) but does NOT redirect away from the current envelope —
  // detail / builder pages are keyed by envelopeId, not by the active holder.
  const { holderId: activeHolderId, setHolderId: setActiveHolderId } = useSelectedHolder();

  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [signingOrder, setSigningOrder] = useState<DocumentSignSigningOrder>('parallel');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Navigation flushes pending Step 2 (recipients) and Step 3 (fields) edits —
  // both auto-save on Back / Review rather than via their own buttons.
  const recipientsRef = useRef<RecipientsTableHandle>(null);
  const fieldsRef = useRef<FieldsEditorHandle>(null);

  // Include-filename auto-title (Step 1).
  const [dsSettings] = useArcheSettings(DOCUMENT_SIGN_ARCHE_ID, DEFAULT_DOCUMENT_SIGN_SETTINGS);
  const filesSettledRef = useRef(false);
  const lastSourceCountRef = useRef(0);

  // Seed the form once per envelope. The autosave below invalidates the
  // envelope query on every mutation, so re-seeding on every `envelope`
  // identity change would race against the user's keystrokes: a refetch
  // landing mid-typing would overwrite the input with the just-saved
  // (older) value and drop the in-flight characters. Keying on envelopeId
  // means the form rehydrates only when the route changes to a different
  // envelope, not on background refetches of the same one.
  const hydratedEnvelopeIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!envelope || !envelopeId) return;
    if (hydratedEnvelopeIdRef.current === envelopeId) return;
    hydratedEnvelopeIdRef.current = envelopeId;
    setTitle(envelope.title);
    setMessage(envelope.message ?? '');
    setSigningOrder(envelope.signingOrder);
  }, [envelope, envelopeId]);

  // When "Include filename" is on, name the envelope from the first uploaded
  // document — but only while the title is still untouched. The settled-once
  // guard distinguishes a fresh upload from files that loaded with the
  // envelope (which must not retrigger the rename).
  useEffect(() => {
    if (filesLoading) return;
    const sourceFiles = files.filter((f) => f.kind === 'source');
    const count = sourceFiles.length;

    if (!filesSettledRef.current) {
      filesSettledRef.current = true;
      lastSourceCountRef.current = count;
      return;
    }
    const wasEmpty = lastSourceCountRef.current === 0;
    lastSourceCountRef.current = count;
    if (!wasEmpty || count === 0) return;
    if (!dsSettings.includeFilename) return;

    const defaultTitle = dsSettings.defaultTitle.trim();
    // "Untouched" means the owner hasn't manually renamed the envelope
    // yet. Two known un-renamed states: the platform default
    // (`PLATFORM_DEFAULT_ENVELOPE_TITLE`) used when no user setting is
    // configured, and the user's own configured `defaultTitle`. Either
    // is fair game to overwrite with the include-filename auto-title.
    const untouched =
      title === PLATFORM_DEFAULT_ENVELOPE_TITLE || (defaultTitle !== '' && title === defaultTitle);
    if (!untouched) return;

    const baseName = sourceFiles[0].fileName.replace(/\.pdf$/i, '').trim();
    const nextTitle = [defaultTitle, baseName].filter(Boolean).join(' ');
    if (!nextTitle || nextTitle === title) return;

    setTitle(nextTitle);
    void updateEnvelope.mutateAsync({ title: nextTitle });
  }, [files, filesLoading, dsSettings, title, updateEnvelope]);

  // The file-count / total-size caps are on source files only (sealed and
  // certificate files are produced later and don't count against them).
  const sourceFiles = files.filter((f) => f.kind === 'source');

  /*
   * Debounced autosave for title / message / signingOrder. Matches the
   * Step 2 (recipients) and Step 3 (fields) autosave cadence so the entire
   * builder works the same way — no Save button, edits land 700ms after
   * the user stops typing. Imperative `flushSave` (defined below) is
   * flushed on navigation as a fallback for an edit that hasn't yet
   * debounced.
   *
   * Hoisted above the early returns so the hooks-order rule isn't violated
   * when the page first mounts with `envelope` still null — without this,
   * the hook gets registered on the first non-skeleton render and React
   * notices an extra hook on that transition.
   *
   * The initial seed run is suppressed by the ref guard so the values
   * just hydrated from the server response aren't immediately echoed back
   * as a no-op write.
   */
  const autosaveSeededRef = useRef(false);
  useEffect(() => {
    // Inline the draft check — `envelope` may be null on early renders,
    // and we can't use the `isDraft` const (declared further down past
    // the early returns).
    if (!envelopeId || !envelope || envelope.status !== 'draft') return;
    if (!autosaveSeededRef.current) {
      autosaveSeededRef.current = true;
      return;
    }
    const handle = setTimeout(() => {
      void flushSave();
    }, 700);
    return () => clearTimeout(handle);
    // Deliberately deps on the user-editable fields only; the mutation
    // hook identity changes on every render and would defeat the
    // debounce. flushSave reads the live values via closure, so its
    // omission from the dep list is safe.
  }, [title, message, signingOrder, envelopeId, envelope]);

  if (error) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-3xl mx-auto p-6 w-full">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      </div>
    );
  }

  if (loading || !envelope) {
    return (
      <div className="flex flex-col h-full">
        <ArcheHeader archeId="arche_document_sign" title="Document Sign">
          <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        </ArcheHeader>
        <div className="max-w-3xl mx-auto p-6 w-full space-y-4">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  const isDraft = envelope.status === 'draft';

  // Imperative save flushed on navigation (Back / Review and send) so we
  // don't lose a stale-debounce edit. The debounced autosave below covers
  // the steady-state happy path.
  async function flushSave() {
    if (!envelopeId) return;
    setSaveError(null);
    try {
      await updateEnvelope.mutateAsync({
        title: title.trim() || PLATFORM_DEFAULT_ENVELOPE_TITLE,
        message: message.length === 0 ? null : message,
        signingOrder,
      });
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    }
  }

  async function handleDelete() {
    if (!envelopeId) return;
    await deleteEnvelope.mutateAsync(envelopeId);
    navigate('/a/arche_document_sign');
  }

  // Both navigations flush pending field and recipient edits. Recipient
  // validation can fail — that keeps the user on the builder with the error
  // visible; field placements just flush.
  async function handleBack() {
    await flushSave();
    await fieldsRef.current?.save();
    const saved = (await recipientsRef.current?.save()) ?? true;
    if (saved) navigate('/a/arche_document_sign');
  }

  async function handleContinueToReview() {
    if (!envelopeId) return;
    await flushSave();
    await fieldsRef.current?.save();
    const saved = (await recipientsRef.current?.save()) ?? true;
    if (saved) navigate(`/a/arche_document_sign/envelope/${envelopeId}/review`);
  }

  return (
    <div className="flex flex-col h-full">
      <ArcheHeader archeId="arche_document_sign" title="Document Sign">
        <HolderSwitcher selectedHolderId={activeHolderId} onChange={setActiveHolderId} />
        <Button variant="outline" size="sm" onClick={handleBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </ArcheHeader>
      <div className="max-w-5xl mx-auto p-6 w-full space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Envelope</h1>
            <p className="text-xs text-muted-foreground">
              Status: {envelope.status.replace('_', ' ')}
            </p>
          </div>
          {isDraft && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleDelete}
              disabled={deleteEnvelope.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete draft
            </Button>
          )}
        </div>

        {/*
         * Workspace callout. Sits above Step 1 so the user knows up front
         * which workspace the envelope belongs to — moved here from a
         * buried field at the bottom of the form when we dropped the
         * "pick a workspace" create-time dialog. The chosen workspace is
         * fixed for the envelope's lifetime; the only way to change it is
         * delete and recreate.
         */}
        <section
          className="border-l-4 border-primary bg-primary/5 rounded-r-md p-4"
          aria-label="Workspace context"
          data-testid="envelope-workspace-banner"
        >
          <p className="text-sm">
            <span className="text-muted-foreground">Workspace: </span>
            <strong data-testid="envelope-holder">{holderName ?? envelope.holderId}</strong>
            <span className="text-xs text-muted-foreground ml-2">
              ({envelope.holderType === 'org' ? 'Organization' : 'Personal'})
            </span>
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Source documents and your signed copy live in this workspace. Can't be changed later —
            delete this draft and start a new envelope to use a different workspace.
          </p>
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Step 1 · Documents</h2>
            <p className="text-xs text-muted-foreground">
              Upload the files your recipients will sign.
            </p>
          </div>
          {envelopeId && !filesLoading && (
            <DocumentGrid envelopeId={envelopeId} files={files} readOnly={!isDraft} />
          )}
          {envelopeId && isDraft && (
            <DocumentUploader envelopeId={envelopeId} currentCount={sourceFiles.length} />
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Step 2 · Recipients</h2>
            <p className="text-xs text-muted-foreground">
              Add up to 20 signers, viewers, or approvers. External signers only need an email.
            </p>
          </div>
          {envelopeId && (
            <RecipientsTable
              ref={recipientsRef}
              envelopeId={envelopeId}
              envelopeHolderId={envelope.holderId}
              signingOrder={signingOrder}
              readOnly={!isDraft}
            />
          )}
        </section>

        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold">Step 3 · Fields</h2>
            <p className="text-xs text-muted-foreground">
              Pick a recipient and a field type, then click on the page to place it. Drag to move,
              drag the corner to resize.
            </p>
          </div>
          {envelopeId && files.length > 0 && (
            <FieldsEditor
              ref={fieldsRef}
              envelopeId={envelopeId}
              files={files}
              readOnly={!isDraft}
            />
          )}
        </section>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="document-sign-title">Title</Label>
            <Input
              id="document-sign-title"
              value={title}
              maxLength={300}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Q1 Service Agreement"
              disabled={!isDraft}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="document-sign-message">Message to recipients</Label>
            <Textarea
              id="document-sign-message"
              value={message}
              maxLength={5000}
              rows={5}
              onChange={(e) => setMessage(e.target.value)}
              disabled={!isDraft}
            />
          </div>

          <fieldset className="space-y-2" disabled={!isDraft}>
            <Label>Signing order</Label>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="signing-order"
                  value="parallel"
                  checked={signingOrder === 'parallel'}
                  onChange={() => setSigningOrder('parallel')}
                />
                Any order (parallel)
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="signing-order"
                  value="sequential"
                  checked={signingOrder === 'sequential'}
                  onChange={() => setSigningOrder('sequential')}
                />
                One at a time (sequential)
              </label>
            </div>
          </fieldset>

          {saveError && <p className="text-sm text-destructive">{saveError}</p>}

          {isDraft && (
            <div className="flex gap-2">
              <Button onClick={handleContinueToReview}>
                <Send className="h-4 w-4 mr-2" />
                Review and send
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
